import { useInfiniteQuery } from '@zg-rentals/react-query5';
import { RentalManagerAccountViewZrmListingsPropertiesWebApiHandler_list } from '@zg-rentals/ts-rental-manager-api-client';

import type {
  ListingPropertiesResponseDto,
  ListingPropertyResponseDto,
} from '@zg-rentals/ts-rental-manager-api-client';

type ListingPropertiesListParams = {
  searchText?: string;
  limit?: number;
};

type UseGetListingPropertiesListOptions = {
  enabled?: boolean;
};

export const useGetListingPropertiesList = (
  params: ListingPropertiesListParams,
  options: UseGetListingPropertiesListOptions = {},
) => {
  const { enabled = true } = options;
  const { searchText = '', limit } = params;

  const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isError, isFetching } =
    useInfiniteQuery<
      ListingPropertiesResponseDto,
      Error,
      { pages: Array<ListingPropertiesResponseDto> },
      Array<string | number | undefined>,
      string
    >({
      queryKey: ['getListingPropertiesList', searchText, limit],
      queryFn: async ({ pageParam }) =>
        RentalManagerAccountViewZrmListingsPropertiesWebApiHandler_list({
          ...(searchText ? { searchText } : {}),
          ...(limit !== undefined ? { limit } : {}),
          ...(pageParam ? { startKeyExclusive: pageParam } : {}),
        }),
      initialPageParam: '',
      getNextPageParam: (lastPage) => (lastPage.pager?.hasMore ? lastPage.pager.endKey : undefined),
      enabled,
      // Hold the previous pages while a new search resolves; the card list only shows skeletons
      // when it has nothing.
      placeholderData: (previousData) => (previousData?.pages?.length ? previousData : undefined),
      meta: {
        tags: ['getListingProperties'],
        context: {
          api: 'RentalManagerAccountViewZrmListingsPropertiesWebApiHandler_list',
          details: 'useGetListingPropertiesList',
        },
      },
    });

  const properties: Array<ListingPropertyResponseDto> = data?.pages.flatMap((page) => page.properties) ?? [];

  return {
    properties,
    error,
    isPending,
    isFetching,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
